import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, Pencil, Trash2, Upload } from "lucide-react";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import UploadModal from "../components/modals/UploadModal";
import EmptyState from "../components/EmptyState";
import DeleteConfirmationModal from "../components/modals/DeleteConfirmationModal";

import { Alert, Spinner, Table, Toast, ToastContainer } from "react-bootstrap";
import { formatDate } from "../utils/dateUtils";
import logger from "../utils/logging";
import ApiService from "../services/ApiService";
import { TemplateFile } from "../models/Templates";
import PageHeader from "../components/PageHeader";

interface FetchError {
    error: any;
    message: string;
    key: number;
}

interface TemplateListProps {
    apiService: ApiService;
    maxFileSizeBytes?: number;
}

const errors: FetchError[] = [];

const TemplateList: React.FC<TemplateListProps> = (props) => {
    const { apiService, maxFileSizeBytes } = props;
    const { t, i18n } = useTranslation(["templates", "common", "pdfPreview"]);
    const sizeFormat = new Intl.NumberFormat(i18n.resolvedLanguage, {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
    });
    const navigate = useNavigate();

    const [fetchErrors, setFetchErrors] = React.useState<FetchError[]>([]);
    const [templates, setTemplates] = React.useState<TemplateFile[] | null>(null);
    const [isFetching, setFetching] = React.useState<boolean>(false);
    const [showUploadModal, setShowUploadModal] = React.useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
    const [templateToDelete, setTemplateToDelete] = React.useState<TemplateFile | null>(null);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [showSuccessToast, setShowSuccessToast] = React.useState<boolean>(false);

    const handleError = (error: any, message: string) => {
        const key = Math.floor(Math.random() * 10000000);
        logger.error("handledError: " + message + ":", error);
        errors.push({ error: error, message: message, key: key });
        logger.debug("number of fetched Errors: " + fetchErrors.length + " new: " + errors.length);
        setFetchErrors([...errors]);
    };

    const removeError = (error: FetchError) => {
        const index = errors.indexOf(error);
        errors.splice(index, 1);

        const indexFetch = fetchErrors.indexOf(error);
        fetchErrors.splice(indexFetch, 1);
    };

    React.useEffect(() => {
        logger.info("start fetch templates");
        setFetching(true);

        apiService
            .getTemplates()
            .then(setTemplates)
            .catch((error) => handleError(error, t("errors.fetch")))
            .finally(() => setFetching(false));
    }, []);

    const handleDeleteClick = (template: TemplateFile) => {
        setTemplateToDelete(template);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!templateToDelete) return;

        setIsDeleting(true);

        try {
            await apiService.deleteTemplate(templateToDelete.id);

            // Successfully deleted
            setShowDeleteModal(false);
            setTemplateToDelete(null);
            setShowSuccessToast(true);

            // Refresh template list
            handleUploadSuccess();
        } catch (error) {
            handleError(error, t("errors.delete"));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setTemplateToDelete(null);
    };

    const handleUploadSuccess = () => {
        // Refresh the template list
        setFetching(true);

        apiService
            .getTemplates()
            .then(setTemplates)
            .catch((error) => handleError(error, t("errors.fetch")))
            .finally(() => setFetching(false));
    };

    return (
        <>
            <PageHeader title={t("title")}>
                <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                    <Upload size={16} />
                    {t("upload.button")}
                </Button>
            </PageHeader>

            {templates != null && templates.length > 0 ? (
                <Row>
                    <Col>
                        <div className="table-responsive">
                            <Table striped className="table-align-middle">
                                <thead>
                                    <tr>
                                        <th scope="col">{t("common:headers.name")}</th>
                                        <th scope="col">{t("common:headers.updatedAt")}</th>
                                        <th scope="col">{t("table.headers.fieldCount")}</th>
                                        <th scope="col">{t("table.headers.size")}</th>
                                        <th scope="col" className="text-nowrap shrink">
                                            {t("common:headers.actions")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {templates.map((template) => (
                                        <tr key={template.id} title={template.id}>
                                            <td>{template.name}</td>
                                            <td>{formatDate(template.updatedAt)}</td>
                                            <th scope="col">{template.fields.length}</th>
                                            <th scope="col">{sizeFormat.format(template.size / 1024 / 1024)} MB</th>
                                            <td className="text-nowrap shrink">
                                                <div className="btn-group" role="group">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => navigate(`/templates/${template.id}/edit`)}
                                                    >
                                                        <Pencil size={16} />
                                                        {t("table.actions.editTemplate")}
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => handleDeleteClick(template)}
                                                        title={t("table.actions.deleteTitle")}
                                                    >
                                                        <Trash2 size={16} />
                                                        {t("table.actions.delete")}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Col>
                </Row>
            ) : (
                !isFetching && (
                    <EmptyState
                        icon={FileText}
                        title={t("empty.title")}
                        message={t("empty.message")}
                        actionLabel={t("upload.button")}
                        onAction={() => setShowUploadModal(true)}
                    />
                )
            )}
            {isFetching && (
                <div className="d-flex gap-3 align-items-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">{t("common:loading")}</span>
                    </Spinner>
                    <span>{t("loading")}</span>
                </div>
            )}
            {fetchErrors.length > 0 && (
                <>
                    {fetchErrors.map((fetchError) => (
                        <Alert
                            key={fetchError.key}
                            variant="danger"
                            onClose={() => removeError(fetchError)}
                            dismissible
                        >
                            <Alert.Heading>{fetchError.message}</Alert.Heading>
                            <p>{fetchError.error.message}</p>
                        </Alert>
                    ))}
                </>
            )}

            <UploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                apiService={apiService}
                onSuccess={handleUploadSuccess}
                maxFileSizeBytes={maxFileSizeBytes}
            />

            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={t("delete.modal.title")}
                message={t("delete.modal.message")}
                itemLabel={t("delete.modal.itemLabel")}
                itemName={templateToDelete?.name}
                isDeleting={isDeleting}
            />

            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showSuccessToast}
                    onClose={() => setShowSuccessToast(false)}
                    delay={3000}
                    autohide
                    bg="success"
                >
                    <Toast.Header>
                        <strong className="me-auto">{t("delete.success.title")}</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{t("delete.success.message")}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default TemplateList;
